import isEmpty from 'lodash/isEmpty'
import type { Dispatch, SetStateAction } from 'react'
import React, { useContext as useContextCore } from 'react'
import { orderGetCartQuery } from './graphql'
import { roundNumber } from '@ztechapps/common.utils'
import type { UseDocumentResult } from '@ztechapps/common.hook'
import { useSearchParam } from 'react-use'
import type { Order } from '@ztechapps/common.contract'
import { OrderStatusEnum } from '@ztechapps/common.contract'
import { useOrderDocument } from '@ztechapps/order.common'

export type ProviderProps = {
  children: React.ReactNode
}

export type ContextType = {
  showLoadingOverlay: boolean
  setShowLoadingOverlay: Dispatch<SetStateAction<boolean>>
  selectedShippingRate: any
  setSelectedShippingRate: (value: any) => void
  hasAddress: boolean
  shippingFee: number
  orderNetAmount: number
  setShippingOptions: (value: any) => void
  shippingOptions: {
    billToMe: boolean
    billToCountryCode: string
  }
  canEdit: boolean
} & UseDocumentResult<Order> &
  Omit<ProviderProps, 'children'>

const context = React.createContext<ContextType>({} as ContextType)
const CoreProvider = context.Provider

export const Provider = ({ children }: ProviderProps) => {
  let orderId = useSearchParam('orderId')

  const [showLoadingOverlay, setShowLoadingOverlay] = React.useState(false)
  const [selectedShippingRate, _setSelectedShippingRate] = React.useState(null)
  const [shippingFee, setShippingFee] = React.useState(0)
  const [orderNetAmount, setOrderNetAmount] = React.useState(0)

  let filter: any = {}
  if (orderId) {
    filter = { orderId }
  }
  const documentResults = useOrderDocument({
    query: orderGetCartQuery,
    filter,
    options: {
      queryOptions: {
        fetchPolicy: 'cache-and-network',
      },
    },
  })

  orderId = documentResults?.document?._id

  const [shippingOptions, _setShippingOptions] = React.useState({ billToMe: false, billToCountryCode: 'US' })

  const setShippingOptions = (value) => {
    _setShippingOptions({ ...shippingOptions, ...value })
  }

  const gross = documentResults?.document?.gross
  const discount = documentResults?.document?.discount ?? 0
  const taxAmount = documentResults?.document?.taxAmount ?? 0
  React.useEffect(() => {
    setOrderNetAmount(gross + taxAmount - discount)
  }, [gross, taxAmount, discount])

  const hasAddress = React.useMemo(
    () => !isEmpty(documentResults?.document?.shippingAddress?.fullAddress),
    [documentResults?.document?.shippingAddress?.fullAddress]
  )

  const setSelectedShippingRate = React.useCallback(
    async (selected) => {
      _setSelectedShippingRate(selected)
      const _shippingFee = roundNumber(selected?.shippingAmount, 2)
      setShippingFee(_shippingFee)
      const net = roundNumber(gross + taxAmount - discount + _shippingFee, 2)
      setOrderNetAmount(net)
    },
    [gross, taxAmount, discount]
  )

  //can only edit the order if the status is In Cart
  const canEdit = documentResults?.document?.status === OrderStatusEnum.InCart

  return (
    <CoreProvider
      value={{
        ...documentResults,
        showLoadingOverlay,
        setShowLoadingOverlay,
        selectedShippingRate,
        setSelectedShippingRate,
        hasAddress,
        shippingFee,
        orderNetAmount,
        setShippingOptions,
        shippingOptions,
        canEdit,
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const useContext = () => useContextCore(context)
